import { get } from '@backmarket/utils/object/get'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import { useSalesCustomerCareLogger } from '@/scopes/care-commons/utils/logger/salesCustomerCare/useSalesCustomerCareLogger'
import {
  RESOLUTION_ENGINE_SERVICE_ENUM,
  RESOLUTION_OPTION_TYPE_ENUM,
} from '~/api/resolution-engine/types/Common.types'
import type { InitiateResolutionPayload } from '~/api/resolution-engine/types/InitiateResolution.types'
import { SALES_CUSTOMER_CARE_SCOPE } from '~/scopes/care-commons/utils/logger/salesCustomerCare/config'
import { useInitiateResolutionApi } from '~/scopes/customer-care/resolution/composables/useInitiateResolutionApi'
import { useUploadAttachments } from '~/scopes/customer-care/resolution/composables/useUploadAttachments'

import type { MachineContext } from '../ResolutionFlow.machine.types'

export async function initiateResolution({
  clientId,
  customerInput,
  diagnosisV2CustomerIssues,
  orderlineId,
  shipment,
}: MachineContext) {
  const { logFeatureError } = useSalesCustomerCareLogger()
  const { uploadAndGetAttachmentsIds } = useUploadAttachments()

  const attachmentHandles = await uploadAndGetAttachmentsIds({
    attachments: customerInput?.pictures,
    customerId: clientId,
  })

  /**
   * `customerInput.resolutionOptions[0]` contains the resolution option
   * selected by the customer. We can safely pick the first item of the array.
   *
   * Furthermore, `customerInput` is an array because at the end of the Return Funnel,
   * The customer can currently ask to be contacted by the seller,
   * which adds a REMOTE_ASSISTANCE resolution option to that array.
   */
  const selectedResolutionOption = customerInput?.resolutionOptions?.[0] || {
    serviceName: RESOLUTION_ENGINE_SERVICE_ENUM.afterSalesPlatform,
    type: RESOLUTION_OPTION_TYPE_ENUM.remoteAssistance,
    actions: [],
  }

  const resolutionRequest: InitiateResolutionPayload['resolutionRequest'] = {
    attachmentHandles,
    isDataRemovalConfirmed: customerInput.areDataRemoved || false,
    message: customerInput.description,
  }

  if (!isEmpty(shipment)) {
    resolutionRequest.shipmentId = shipment?.id
  }

  const payload = await useInitiateResolutionApi({
    declaredProblem: {
      declaredWarrantyState: customerInput.declaredWarrantyState,
      orderlineId,
      customerIssues: diagnosisV2CustomerIssues,
    },
    resolutionOption: selectedResolutionOption,
    resolutionRequest,
  })

  const resolution = get(payload, 'resolution')

  if (!isEmpty(resolution)) {
    return { resolution }
  }

  logFeatureError({
    errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.resolutionFlow} no resolution initiated on initiate resolution service`,
    errorDetail: {
      requestBody: {
        declaredProblem: {
          declaredWarrantyState: customerInput.declaredWarrantyState,
          orderlineId,
          customerIssues: diagnosisV2CustomerIssues,
        },
        resolutionOption: selectedResolutionOption,
        resolutionRequest,
      },
    },
    featureName: 'resolution_flow_no_resolution_initiated',
  })

  throw new Error('No resolution initiated')
}
